import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import ReactSwitchImport from 'react-switch';
import { configSelector } from '../../../redux/selectors/system/config-selector';
import { regionIdSelector } from '../../../redux/selectors/system/config-region-id-selector';
import { Config } from '../../../types/config/config';
import { PropertyTypeOption } from '../../../types/common/option';
import { useBrokerWorkflow } from '../../../hooks/useBrokerWorkflow';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const Switch = ReactSwitchImport as any;

interface BrokerWorkflowFormValues {
    id: string;
    companyId: string;
    miqMaster: boolean;
    salesforceMaster: boolean;
    miq: { [key: string]: boolean };
    salesforce: { [key: string]: boolean };
}

type IntegrationKey = 'miq' | 'salesforce';

interface ToastState {
    type: 'success' | 'error' | 'info';
    message: string;
}

const INTEGRATION_LABELS: { [key in IntegrationKey]: string } = {
    miq: 'MIQ',
    salesforce: 'CIQ'
};

type IntegrationErrors = { [key in IntegrationKey]?: string };

interface FormErrors {
    companyId?: string;
    integration?: IntegrationErrors;
}

const buildIntegrationErrors = (values: BrokerWorkflowFormValues): IntegrationErrors => {
    const errors: IntegrationErrors = {};
    if (values.miqMaster && !Object.values(values.miq).some(Boolean)) {
        errors.miq = `Select at least one property type for ${INTEGRATION_LABELS.miq}.`;
    }
    if (values.salesforceMaster && !Object.values(values.salesforce).some(Boolean)) {
        errors.salesforce = `Select at least one property type for ${INTEGRATION_LABELS.salesforce}.`;
    }
    return errors;
};

const buildInitialValues = (propertyTypes: PropertyTypeOption[]): BrokerWorkflowFormValues => {
    const miq: { [key: string]: boolean } = {};
    const salesforce: { [key: string]: boolean } = {};

    propertyTypes.forEach((propertyType: PropertyTypeOption) => {
        const key = String(propertyType.value);
        miq[key] = false;
        salesforce[key] = false;
    });

    return {
        id: '',
        companyId: '',
        miqMaster: false,
        salesforceMaster: false,
        miq,
        salesforce
    };
};

const areWorkflowFlagsEqual = (left: { [key: string]: boolean }, right: { [key: string]: boolean }): boolean => {
    const leftKeys = Object.keys(left);
    return leftKeys.length === Object.keys(right).length
        && leftKeys.every(key => left[key] === right[key]);
};

const areFormValuesEqual = (left: BrokerWorkflowFormValues, right: BrokerWorkflowFormValues): boolean => (
    left.id === right.id
    && left.companyId === right.companyId
    && left.miqMaster === right.miqMaster
    && left.salesforceMaster === right.salesforceMaster
    && areWorkflowFlagsEqual(left.miq, right.miq)
    && areWorkflowFlagsEqual(left.salesforce, right.salesforce)
);

const AdminBrokerWorkflowTab: FC = () => {
    const config: Config = useSelector(configSelector);
    const regionId = useSelector(regionIdSelector) as string | undefined;
    const propertyTypes: PropertyTypeOption[] = (config && config.propertyType && config.propertyType.options)
        ? config.propertyType.options
        : [];

    const {
        formValues: fetchedFormValues,
        loading,
        error,
        isSaving,
        fetchBrokerWorkflow,
        saveBrokerWorkflow,
        clearError
    } = useBrokerWorkflow();

    // Derive initVals synchronously so the form always reflects fetched backend data
    // merged with the property type keys from config.
    const initVals = useMemo((): BrokerWorkflowFormValues => {
        const base = buildInitialValues(propertyTypes);
        if (!fetchedFormValues) { return base; }
        return {
            id: fetchedFormValues.id,
            companyId: fetchedFormValues.companyId,
            miqMaster: fetchedFormValues.miqMaster,
            salesforceMaster: fetchedFormValues.salesforceMaster,
            miq: { ...base.miq, ...fetchedFormValues.miq },
            salesforce: { ...base.salesforce, ...fetchedFormValues.salesforce },
        };
    }, [fetchedFormValues, propertyTypes]);

    const [formValues, setFormValues] = useState<BrokerWorkflowFormValues>(initVals);
    const [initialFormValues, setInitialFormValues] = useState<BrokerWorkflowFormValues>(initVals);
    const [toast, setToast] = useState<ToastState | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);

    useEffect(() => {
        setFormValues(initVals);
        setInitialFormValues(initVals);
    }, [initVals]);

    useEffect(() => {
        if (config && config.siteId) {
            fetchBrokerWorkflow(config.siteId);
        }
    }, [config && config.siteId, fetchBrokerWorkflow]);

    useEffect(() => {
        if (error) {
            setToast({ type: 'error', message: error });
            clearError();
        }
    }, [error, clearError]);

    useEffect(() => {
        if (!toast) { return; }
        const timer = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(timer);
    }, [toast]);

    const handleCompanyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setFormValues(prev => ({ ...prev, companyId: value }));
        setErrors(prev => (prev.companyId ? { ...prev, companyId: undefined } : prev));
    };

    const handleMasterToggle = (key: IntegrationKey) => (checked: boolean) => {
        const next: BrokerWorkflowFormValues = {
            ...formValues,
            ...(key === 'miq' ? { miqMaster: checked } : { salesforceMaster: checked })
        };
        setFormValues(next);
        // Only live-update the message if Save has already surfaced one — never raise it just from toggling.
        setErrors(prev => (prev.integration ? { ...prev, integration: buildIntegrationErrors(next) } : prev));
    };

    const handlePropertyToggle = (key: IntegrationKey, propertyKey: string) => (checked: boolean) => {
        const next: BrokerWorkflowFormValues = {
            ...formValues,
            [key]: { ...formValues[key], [propertyKey]: checked }
        };
        setFormValues(next);
        setErrors(prev => (prev.integration ? { ...prev, integration: buildIntegrationErrors(next) } : prev));
    };

    const performSave = useCallback(async () => {
        const success = await saveBrokerWorkflow(
            formValues,
            regionId || '',
            (config && config.siteId) || '',
            (config && config.defaultCultureCode) || ''
        );
        if (success) {
            setToast({ type: 'success', message: 'Broker workflow settings saved successfully.' });
        }
    }, [saveBrokerWorkflow, formValues, regionId, config]);

    const handleSave = useCallback(() => {
        const nextErrors: FormErrors = {};

        if (!formValues.companyId || formValues.companyId.trim().length === 0) {
            nextErrors.companyId = 'Company ID is required.';
        }
        const integrationErrors = buildIntegrationErrors(formValues);
        if (Object.keys(integrationErrors).length > 0) {
            nextErrors.integration = integrationErrors;
        }

        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        if (initialFormValues.id && areFormValuesEqual(formValues, initialFormValues)) {
            setToast({ type: 'info', message: 'Make changes before saving.' });
            return;
        }

        setShowSaveConfirm(true);
    }, [formValues, initialFormValues]);

    const handleConfirmSave = useCallback(() => {
        setShowSaveConfirm(false);
        performSave();
    }, [performSave]);

    const handleCancelSave = useCallback(() => {
        setShowSaveConfirm(false);
    }, []);

    return (
        <Wrapper>
            <PageHeader>
                <PageTitle>Broker Workflow</PageTitle>
                <PageSubtitle>Configure integration eligibility by company and property type.</PageSubtitle>
            </PageHeader>

            <Card>
                {loading ? (
                    <SkeletonArea>
                        <SkBar style={{ width: 120, height: 12 }} />
                        <SkBar style={{ width: '100%', height: 46, marginTop: 8 }} />

                        <IntegrationsList style={{ marginTop: 24 }}>
                            {[0, 1].map((i) => (
                                <IntegrationCard key={i}>
                                    <IntegrationCardHeader>
                                        <SkBar style={{ width: 64, height: 18 }} />
                                        <SkBar style={{ width: 46, height: 24, borderRadius: 12 }} />
                                    </IntegrationCardHeader>
                                    <RequirementsArea>
                                        <PropertyGrid>
                                            <SkBar style={{ width: '70%', height: 16 }} />
                                            <SkBar style={{ width: '70%', height: 16 }} />
                                            <SkBar style={{ width: '70%', height: 16 }} />
                                            <SkBar style={{ width: '70%', height: 16 }} />
                                        </PropertyGrid>
                                    </RequirementsArea>
                                </IntegrationCard>
                            ))}
                        </IntegrationsList>
                    </SkeletonArea>
                ) : (
                    <>
                        <FieldGroup>
                            <FieldLabel htmlFor="broker-workflow-company-id">
                                Company ID <RequiredMark>*</RequiredMark>
                            </FieldLabel>
                            <TextInput
                                id="broker-workflow-company-id"
                                placeholder="Enter Company ID"
                                value={formValues.companyId}
                                onChange={handleCompanyIdChange}
                                disabled={isSaving}
                                hasError={!!errors.companyId}
                                autoComplete="off"
                            />
                            {errors.companyId && <FieldError>{errors.companyId}</FieldError>}
                        </FieldGroup>

                        <IntegrationsList>
                            {(Object.keys(INTEGRATION_LABELS) as IntegrationKey[]).map((key) => {
                                const master = key === 'miq' ? formValues.miqMaster : formValues.salesforceMaster;
                                return (
                                    <IntegrationBlock key={key}>
                                        <IntegrationCard>
                                            <IntegrationCardHeader>
                                                <TabLabel $active={master}>{INTEGRATION_LABELS[key]}</TabLabel>
                                                <Switch
                                                    onChange={handleMasterToggle(key)}
                                                    checked={master}
                                                    disabled={isSaving}
                                                    handleDiameter={20}
                                                    uncheckedIcon={false}
                                                    checkedIcon={false}
                                                    onColor="#006A4D"
                                                    offColor="#cccccc"
                                                    height={24}
                                                    width={46}
                                                />
                                            </IntegrationCardHeader>

                                            <RequirementsArea>
                                                <PropertyGrid>
                                                    {propertyTypes.map((propertyType: PropertyTypeOption) => {
                                                        const propertyKey = String(propertyType.value);
                                                        const disabled = !master || isSaving;
                                                        return (
                                                            <PropertyCheckboxRow
                                                                key={`${key}-${propertyKey}`}
                                                                $disabled={disabled}
                                                            >
                                                                <PropertyCheckboxBox>
                                                                    <PropertyCheckboxInput
                                                                        type="checkbox"
                                                                        checked={!!formValues[key][propertyKey]}
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                            handlePropertyToggle(key, propertyKey)(e.target.checked)
                                                                        }
                                                                        disabled={disabled}
                                                                    />
                                                                    <PropertyCheckboxMark />
                                                                </PropertyCheckboxBox>
                                                                <PropertyCheckboxText $disabled={disabled}>
                                                                    {propertyType.label}
                                                                </PropertyCheckboxText>
                                                            </PropertyCheckboxRow>
                                                        );
                                                    })}
                                                </PropertyGrid>
                                            </RequirementsArea>
                                        </IntegrationCard>
                                        {errors.integration && errors.integration[key] && (
                                            <IntegrationFieldError>{errors.integration[key]}</IntegrationFieldError>
                                        )}
                                    </IntegrationBlock>
                                );
                            })}
                        </IntegrationsList>

                        <SaveHint>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>
                                A property type can be enabled for enquiry creation in only one application at a time.
                                Enabling the same property type in both MIQ and CIQ may result in duplicate enquiry records.
                            </span>
                        </SaveHint>

                        <ActionRow>
                            <SaveButton onClick={handleSave} disabled={isSaving} type="button">
                                {isSaving && <ButtonSpinner />}
                                {isSaving ? 'Saving…' : 'Save Settings'}
                            </SaveButton>
                        </ActionRow>
                    </>
                )}
            </Card>

            {toast && (
                <ToastWrap variant={toast.type}>
                    <ToastIcon variant={toast.type}>{toast.type === 'success' ? '✓' : toast.type === 'error' ? '!' : 'i'}</ToastIcon>
                    <ToastText>{toast.message}</ToastText>
                    <ToastClose onClick={() => setToast(null)} aria-label="Dismiss">&times;</ToastClose>
                </ToastWrap>
            )}

            <ConfirmModal
                open={showSaveConfirm}
                titleId="broker-workflow-confirm-save-title"
                title="Confirm Save"
                message="Are you sure you want to save these broker workflow settings?"
                icon={<FontAwesomeIcon icon={faExclamationTriangle} color="#d97706" size="lg" />}
                confirmLabel="Save"
                onConfirm={handleConfirmSave}
                onCancel={handleCancelSave}
            />
        </Wrapper>
    );
};

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;
const slideIn = keyframes`
  from { transform: translateY(-8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Wrapper = styled.div`
    min-height: 100%;
    background: #ffffff;
    font-family: 'Futura Md BT', helvetica, arial, sans-serif;
    padding: 24px 32px 48px;
    box-sizing: border-box;
`;

const PageHeader = styled.div`
    max-width: 800px;
    margin-bottom: 20px;
`;

const PageTitle = styled.h2`
    font-size: 1.75rem;
    font-weight: 700;
    color: #0c5a45;
    margin: 0 0 6px;
    letter-spacing: -0.01em;
`;

const PageSubtitle = styled.p`
    font-size: 1rem;
    color: #6b7280;
    margin: 0;
`;

const Card = styled.div`
    max-width: 800px;
    background: #ffffff;
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    padding: 28px;
`;

const FieldGroup = styled.div`
    margin-bottom: 24px;
`;

const FieldLabel = styled.label`
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 8px;
`;

const RequiredMark = styled.span`
    color: #c0392b;
`;

const TextInput = styled.input<{ hasError?: boolean }>`
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    font-size: 1rem;
    color: #1a1a1a;
    background: #ffffff;
    border: 1px solid ${({ hasError }) => hasError ? '#c0392b' : '#cccccc'};
    border-radius: 6px;
    outline: none;
    font-family: inherit;
    transition: border-color 140ms;

    &::placeholder { color: #9ca3af; }
    &:focus { border-color: ${({ hasError }) => hasError ? '#c0392b' : '#006A4D'}; }
    &:disabled { background: #f3f4f6; cursor: not-allowed; }
`;

const FieldError = styled.p`
    margin: 8px 0 0;
    font-size: 0.875rem;
    font-weight: 500;
    color: #c0392b;
`;

const SaveHint = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 24px;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #4b5563;

    svg { margin-top: 3px; flex-shrink: 0; color: #d97706; }
`;

const IntegrationsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const IntegrationBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const IntegrationFieldError = styled(FieldError)`
    margin: 0;
`;

const IntegrationCard = styled.div`
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    overflow: hidden;
`;

const IntegrationCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 20px;
    box-sizing: border-box;
    background: #eef2f0;
`;

const TabLabel = styled.span<{ $active: boolean }>`
    font-size: 1.1875rem;
    font-weight: ${({ $active }) => $active ? 800 : 600};
    color: ${({ $active }) => $active ? '#0c5a45' : '#8a978f'};
`;

const RequirementsArea = styled.div`
    padding: 20px;
`;

const PropertyGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
`;

const PropertyCheckboxRow = styled.label<{ $disabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
`;

const PropertyCheckboxBox = styled.span`
    position: relative;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
`;

const PropertyCheckboxInput = styled.input`
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    cursor: inherit;
    z-index: 1;
`;

const PropertyCheckboxMark = styled.span`
    position: absolute;
    inset: 0;
    background: #ffffff;
    border: 1px solid #cccccc;
    border-radius: 3px;
    pointer-events: none;
    transition: border-color 120ms;

    &::after {
        content: '';
        position: absolute;
        display: none;
        left: 6px;
        top: 2px;
        width: 5px;
        height: 10px;
        border: solid #006A4D;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
    }

    ${PropertyCheckboxInput}:checked ~ & {
        border-color: #006A4D;
    }
    ${PropertyCheckboxInput}:checked ~ &::after {
        display: block;
    }
    ${PropertyCheckboxInput}:disabled ~ & {
        background: #f3f4f6;
        border-color: #e0e0e0;
    }
`;

const PropertyCheckboxText = styled.span<{ $disabled?: boolean }>`
    font-size: 1rem;
    font-weight: 500;
    color: ${({ $disabled }) => $disabled ? '#9ca3af' : '#1a1a1a'};
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: 24px;
`;

const SaveButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #006A4D;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 12px 28px;
    font-size: 1.0625rem;
    font-weight: 400;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 140ms, opacity 140ms;

    &:hover:not(:disabled) { background-color: #005440; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ButtonSpinner = styled.span`
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

/* ─── Skeleton (loading state) ───────────────────────────────────────── */
const SkeletonArea = styled.div`
    display: flex;
    flex-direction: column;
`;

const SkBar = styled.div`
    border-radius: 6px;
    background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s infinite;
`;

/* ─── Toast ──────────────────────────────────────────────────────────── */
const toastColors = {
    success: { bg: '#e8f5ef', border: '#b7e3cd', text: '#0c5a45', icon: '#006a4d' },
    error: { bg: '#fdecea', border: '#f4c7c1', text: '#8a2f22', icon: '#c0392b' },
    info: { bg: '#eaf3f8', border: '#b9d9e8', text: '#24566f', icon: '#2d7a9b' },
};

const ToastWrap = styled.div<{ variant: ToastState['type'] }>`
    position: fixed;
    top: 145px;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 380px;
    padding: 12px 14px;
    border-radius: 8px;
    background: ${({ variant }) => toastColors[variant].bg};
    border: 1px solid ${({ variant }) => toastColors[variant].border};
    box-shadow: 0 4px 14px rgba(16, 24, 40, 0.12);
    animation: ${slideIn} 220ms ease-out;
    z-index: 1000;
`;

const ToastIcon = styled.span<{ variant: ToastState['type'] }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${({ variant }) => toastColors[variant].icon};
    color: #ffffff;
    font-size: 0.75rem;
    font-weight: 700;
`;

const ToastText = styled.span`
    font-size: 0.9375rem;
    font-weight: 500;
    color: #1a1a1a;
    line-height: 1.4;
`;

const ToastClose = styled.button`
    background: none;
    border: none;
    font-size: 1.125rem;
    line-height: 1;
    cursor: pointer;
    color: inherit;
    opacity: 0.6;
    padding: 0;
    margin-left: 4px;

    &:hover { opacity: 1; }
`;

export default AdminBrokerWorkflowTab;
